import React, { useMemo, useState } from 'react';

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

type Model = 'freelance' | 'productized' | 'saas';

const IncomeEstimator: React.FC = () => {
  const [model, setModel] = useState<Model>('freelance');
  const [hoursPerWeek, setHoursPerWeek] = useState(10);
  const [hourlyRate, setHourlyRate] = useState(50); // £
  const [packagePrice, setPackagePrice] = useState(800); // £
  const [leadsPerMonth, setLeadsPerMonth] = useState(10);
  const [closeRate, setCloseRate] = useState(20); // %
  const [mrrPrice, setMrrPrice] = useState(19); // £
  const [trialToPaid, setTrialToPaid] = useState(10); // %
  const [trialsPerMonth, setTrialsPerMonth] = useState(50);

  const monthly = useMemo(() => {
    if (model === 'freelance') {
      const billablePct = 0.6; // some time is marketing/admin
      const billableHours = hoursPerWeek * 4.3 * billablePct;
      return billableHours * hourlyRate;
    }
    if (model === 'productized') {
      const deals = Math.round((leadsPerMonth * (closeRate / 100)));
      return deals * packagePrice;
    }
    // saas
    const paidSubs = trialsPerMonth * (trialToPaid / 100);
    return paidSubs * mrrPrice;
  }, [model, hoursPerWeek, hourlyRate, packagePrice, leadsPerMonth, closeRate, mrrPrice, trialToPaid, trialsPerMonth]);

  const yearly = monthly * 12;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button onClick={() => setModel('freelance')} className={`px-3 py-1 rounded-full text-sm border ${model==='freelance'?'bg-indigo-600 text-white border-indigo-600':'bg-white text-gray-800 border-gray-300'}`}>Freelance</button>
        <button onClick={() => setModel('productized')} className={`px-3 py-1 rounded-full text-sm border ${model==='productized'?'bg-indigo-600 text-white border-indigo-600':'bg-white text-gray-800 border-gray-300'}`}>Productized</button>
        <button onClick={() => setModel('saas')} className={`px-3 py-1 rounded-full text-sm border ${model==='saas'?'bg-indigo-600 text-white border-indigo-600':'bg-white text-gray-800 border-gray-300'}`}>Micro‑SaaS</button>
      </div>

      {model === 'freelance' && (
        <div className="grid sm:grid-cols-3 gap-4">
          <label className="block">
            <span className="text-sm text-gray-700">Hours/week</span>
            <input type="range" min={1} max={60} value={hoursPerWeek} onChange={(e)=>setHoursPerWeek(clamp(parseInt(e.target.value,10)||0,1,60))} className="w-full"/>
            <span className="text-xs text-gray-600">{hoursPerWeek} hrs</span>
          </label>
          <label className="block">
            <span className="text-sm text-gray-700">Hourly rate (£)</span>
            <input type="range" min={10} max={250} value={hourlyRate} onChange={(e)=>setHourlyRate(clamp(parseInt(e.target.value,10)||0,10,250))} className="w-full"/>
            <span className="text-xs text-gray-600">£{hourlyRate}/hr</span>
          </label>
        </div>
      )}

      {model === 'productized' && (
        <div className="grid sm:grid-cols-3 gap-4">
          <label className="block">
            <span className="text-sm text-gray-700">Leads/month</span>
            <input type="range" min={1} max={200} value={leadsPerMonth} onChange={(e)=>setLeadsPerMonth(clamp(parseInt(e.target.value,10)||0,1,200))} className="w-full"/>
            <span className="text-xs text-gray-600">{leadsPerMonth}</span>
          </label>
          <label className="block">
            <span className="text-sm text-gray-700">Close rate (%)</span>
            <input type="range" min={1} max={80} value={closeRate} onChange={(e)=>setCloseRate(clamp(parseInt(e.target.value,10)||0,1,80))} className="w-full"/>
            <span className="text-xs text-gray-600">{closeRate}%</span>
          </label>
          <label className="block">
            <span className="text-sm text-gray-700">Package price (£)</span>
            <input type="range" min={100} max={10000} value={packagePrice} onChange={(e)=>setPackagePrice(clamp(parseInt(e.target.value,10)||0,100,10000))} className="w-full"/>
            <span className="text-xs text-gray-600">£{packagePrice}</span>
          </label>
        </div>
      )}

      {model === 'saas' && (
        <div className="grid sm:grid-cols-3 gap-4">
          <label className="block">
            <span className="text-sm text-gray-700">Trials/month</span>
            <input type="range" min={10} max={1000} value={trialsPerMonth} onChange={(e)=>setTrialsPerMonth(clamp(parseInt(e.target.value,10)||0,10,1000))} className="w-full"/>
            <span className="text-xs text-gray-600">{trialsPerMonth}</span>
          </label>
          <label className="block">
            <span className="text-sm text-gray-700">Trial → paid (%)</span>
            <input type="range" min={1} max={60} value={trialToPaid} onChange={(e)=>setTrialToPaid(clamp(parseInt(e.target.value,10)||0,1,60))} className="w-full"/>
            <span className="text-xs text-gray-600">{trialToPaid}%</span>
          </label>
          <label className="block">
            <span className="text-sm text-gray-700">MRR price (£)</span>
            <input type="range" min={5} max={199} value={mrrPrice} onChange={(e)=>setMrrPrice(clamp(parseInt(e.target.value,10)||0,5,199))} className="w-full"/>
            <span className="text-xs text-gray-600">£{mrrPrice}/mo</span>
          </label>
        </div>
      )}

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl border border-gray-200 p-4 bg-white">
          <p className="text-xs uppercase text-gray-500">Estimated monthly</p>
          <p className="text-2xl font-semibold text-gray-900">£{Math.round(monthly).toLocaleString()}</p>
        </div>
        <div className="rounded-xl border border-gray-200 p-4 bg-white">
          <p className="text-xs uppercase text-gray-500">Estimated yearly</p>
          <p className="text-2xl font-semibold text-gray-900">£{Math.round(yearly).toLocaleString()}</p>
        </div>
      </div>
      <p className="mt-3 text-xs text-gray-500">These are illustrative estimates, not guarantees. Actual results depend on execution, niche, and pricing.</p>
    </div>
  );
};

export default IncomeEstimator;
